import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { toast } from '@/hooks/use-toast';
import { logActivity } from '@/lib/activity-log';
import { useLocation } from '@/contexts/LocationContext';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { VisitService, type VisitRecord, type CreateVisitData } from '@/lib/visit-service';

export type Visit = VisitRecord & {
  products?: VisitProduct[];
  photos?: VisitPhoto[];
  visitNotes?: VisitNote[]; // Renamed to avoid conflict with notes field from VisitRecord
};

export type VisitProduct = {
  id: string;
  product_id: string;
  event_id: string; // Changed from visit_id to event_id
  quantity: number;
  noted_at: string;
  product?: {
    id: string;
    name: string;
    category: string;
    price: number;
  };
};

export type VisitPhoto = {
  id: string;
  event_id: string; // Changed from visit_id to event_id
  media_link_id: string;
  taken_at: string;
  url?: string;
};

export type VisitNote = {
  id: string;
  event_id: string; // Changed from visit_id to event_id
  content: string;
  created_at: string;
  author_id?: string;
  author?: {
    full_name?: string;
  };
};

export type TemporaryVisit = {
  id: string;
  visitId: string;
  timestamp: string;
  location: string;
  visitType: string;
  notes: string;
  productId?: string;
};

export type UnmatchedItem = {
  id: string;
  thumbnail: string;
  timestamp: string;
  location: string;
  timeToDelete: number; // hours remaining
  candidates: UnmatchedCandidate[];
  face_recognition_id?: string;
  visit_id?: string;
};

export type UnmatchedCandidate = {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  customer_id: string;
};

export const useVisits = () => {
  const { companyId, user } = useAuth();
  const { currentLocation } = useLocation();
  const queryClient = useQueryClient();

  // Get all visit records
  const {
    data: visits = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ['visit-records', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      return await VisitService.getVisits(companyId);
    },
    enabled: !!companyId,
  });

  // Get single visit with full details
  const getVisit = (visitId: string) => {
    return useQuery({
      queryKey: ['visit-record', visitId],
      queryFn: async () => {
        if (!visitId) return null;
        return await VisitService.getVisit(visitId);
      },
      enabled: !!visitId,
    });
  };

  // Get visit products
  const getVisitProducts = (visitId: string) => {
    return useQuery({
      queryKey: ['visit-products', visitId],
      queryFn: async () => {
        if (!visitId) return [];
        
        try {
          // Query visit_products table with product details
          const { data, error } = await supabase
            .from('visit_products' as any)
            .select(`
              *,
              product:products(
                id,
                name,
                category,
                price,
                item_code
              )
            `)
            .eq('visit_id', visitId)
            .order('created_at', { ascending: false });

          if (error) {
            console.warn('visit_products table not found:', error);
            return [];
          }
          return data || [];
        } catch (error) {
          console.warn('Error fetching visit products:', error);
          return [];
        }
      },
      enabled: !!visitId,
    });
  };

  // Get visit photos
  const getVisitPhotos = (visitId: string) => {
    return useQuery({
      queryKey: ['visit-photos', visitId],
      queryFn: async () => {
        if (!visitId) return [];
        
        try {
          // Try visit_photos table first
          const { data, error } = await supabase
            .from('visit_photos' as any)
            .select(`
              *,
              media_link:media_links(
                id,
                bucket,
                path,
                mime,
                created_at
              )
            `)
            .eq('visit_id', visitId)
            .order('taken_at', { ascending: false });

          if (error) {
            // Fallback to media_links table for backward compatibility
            const { data: fallbackData, error: fallbackError } = await supabase
              .from('media_links')
              .select('*')
              .eq('owner_table', 'visits')
              .eq('owner_id', visitId)
              .order('created_at', { ascending: false });
            
            if (fallbackError) {
              console.warn('Error fetching visit photos:', fallbackError);
              return [];
            }
            return fallbackData || [];
          }
          
          return data || [];
        } catch (error) {
          console.warn('Error fetching visit photos:', error);
          return [];
        }
      },
      enabled: !!visitId,
    });
  };

  // Get visit notes
  const getVisitNotes = (visitId: string) => {
    return useQuery({
      queryKey: ['visit-notes', visitId],
      queryFn: async () => {
        if (!visitId) return [];
        
        try {
          // Query visit_notes table with author details
          const { data, error } = await supabase
            .from('visit_notes' as any)
            .select(`
              *,
              author:user_profiles(
                id,
                full_name
              )
            `)
            .eq('visit_id', visitId)
            .order('created_at', { ascending: false });

          if (error) {
            console.warn('visit_notes table not found:', error);
            return [];
          }
          
          return data || [];
        } catch (error) {
          console.warn('Error fetching visit notes:', error);
          return [];
        }
      },
      enabled: !!visitId,
    });
  };

  // Get open visits (in_progress status)
  const useOpenVisits = () => {
    return useQuery({
      queryKey: ['visit-records', 'open', companyId],
      queryFn: async () => {
        if (!companyId) return [];
        
        const allVisits = await VisitService.getVisits(companyId);
        // Filter for open visits (visits without an end time)
        return allVisits.filter(visit => !visit.ended_at);
      },
      enabled: !!companyId,
    });
  };

  // Start visit mutation
  const startVisitMutation = useMutation({
    mutationFn: async (visitData: CreateVisitData) => {
      if (!companyId) throw new Error('No company selected');
      return await VisitService.createVisit(companyId, visitData);
    },
    onSuccess: async (data, variables) => {
      // Log activity
      if (companyId) {
        await logActivity({
          action: variables.ended_at ? 'create' : 'start',
          entity: 'visit',
          entityId: data.id,
          detail: {
            customer_id: variables.customer_id,
            source: variables.source,
            temporary: variables.temporary || false,
            location: variables.location,
          },
          locationId: currentLocation?.id || null,
          companyId,
          userId: user?.id || null,
        });
      }

      // Check if it's a manual visit (has ended_at set) to show appropriate message
      const isManualVisit = variables.source === 'manual' && variables.ended_at;
      toast({
        title: isManualVisit ? "Ziyaret kaydedildi" : "Ziyaret başlatıldı",
        description: isManualVisit ? "Yeni ziyaret başarıyla kaydedildi." : "Yeni ziyaret başarıyla başlatıldı.",
      });
      queryClient.invalidateQueries({ queryKey: ['visit-records'] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Ziyaret başlatılırken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error starting visit:', error);
    },
  });

  // End visit mutation
  const endVisitMutation = useMutation({
    mutationFn: async (visitId: string) => {
      await VisitService.endVisit(visitId);
      
      // Clean up temporary visit data if exists
      localStorage.removeItem(`temp_visit_${visitId}`);
      
      return visitId;
    },
    onSuccess: async (visitId) => {
      // Log activity
      if (companyId) {
        await logActivity({
          action: 'end',
          entity: 'visit',
          entityId: visitId,
          detail: {},
          locationId: currentLocation?.id || null,
          companyId,
          userId: user?.id || null,
        });
      }

      toast({
        title: "Ziyaret tamamlandı",
        description: "Ziyaret başarıyla tamamlandı.",
      });
      queryClient.invalidateQueries({ queryKey: ['visit-records'] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Ziyaret tamamlanırken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error ending visit:', error);
    },
  });

  // Link temporary visit to customer
  const linkVisitToCustomerMutation = useMutation({
    mutationFn: async ({ visitId, customerId }: { visitId: string; customerId: string }) => {
      await VisitService.linkVisitToCustomer(visitId, customerId);
      
      // Clean up temporary visit data
      localStorage.removeItem(`temp_visit_${visitId}`);

      return { visitId, customerId };
    },
    onSuccess: () => {
      toast({
        title: "Ziyaret bağlandı",
        description: "Ziyaret başarıyla müşteriye bağlandı.",
      });
      queryClient.invalidateQueries({ queryKey: ['visit-records'] });
      queryClient.invalidateQueries({ queryKey: ['temporaryVisits'] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Ziyaret bağlanırken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error linking visit:', error);
    },
  });

  // Get temporary visit data
  const getTemporaryVisitData = (visitId: string) => {
    const data = localStorage.getItem(`temp_visit_${visitId}`);
    return data ? JSON.parse(data) : null;
  };

  const isTemporaryVisit = (visit: VisitRecord) => {
    const status = String(visit.status || '').toUpperCase();
    return !visit.customer_id && status === 'PENDING_LABEL';
  };

  const getVisitDisplayName = (visit: VisitRecord) => {
    if (visit.customer?.first_name || visit.customer?.last_name) {
      return `${visit.customer.first_name || ''} ${visit.customer.last_name || ''}`.trim();
    }
    return `Ziyaretçi-${visit.id.slice(-3).toUpperCase()}`;
  };

  const isManualVisit = (visit: VisitRecord) => {
    return visit.source === 'manual';
  };

  const isFaceRecognitionVisit = (visit: VisitRecord) => {
    return visit.source === 'face_recognition';
  };

  const getVisitSourceText = (visit: VisitRecord) => {
    switch (visit.source) {
      case 'face_recognition':
        return 'Yüz Tanıma';
      case 'qr_scan':
        return 'QR Kod';
      case 'manual':
      default:
        return 'Manuel';
    }
  };

  // Upload photo to visit mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ visitId, file, caption }: { visitId: string; file: File; caption?: string }) => {
      if (!companyId) throw new Error('No company selected');
      return await VisitService.uploadAndAddPhoto(companyId, visitId, file, caption);
    },
    onSuccess: (result, variables) => {
      if (result.success) {
        toast({
          title: "Fotoğraf yüklendi",
          description: "Fotoğraf başarıyla ziyarete eklendi.",
        });
        // Invalidate visit photos to refresh the list
        queryClient.invalidateQueries({ queryKey: ['visit-photos', variables.visitId] });
      } else {
        toast({
          title: "Hata",
          description: result.error || "Fotoğraf yüklenirken bir hata oluştu.",
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Fotoğraf yüklenirken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error uploading photo:', error);
    },
  });

  // Add product to visit mutation
  const addProductToVisitMutation = useMutation({
    mutationFn: async ({ visitId, productId, notes }: { visitId: string; productId: string; notes?: string }) => {
      if (!companyId) throw new Error('No company selected');
      await VisitService.addProductToVisit(companyId, visitId, productId, notes);
      return { visitId, productId };
    },
    onSuccess: (data) => {
      toast({
        title: "Ürün eklendi",
        description: "Ürün başarıyla ziyarete eklendi.",
      });
      queryClient.invalidateQueries({ queryKey: ['visit-products', data.visitId] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Ürün eklenirken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error adding product to visit:', error);
    },
  });

  // Add note to visit mutation
  const addNoteToVisitMutation = useMutation({
    mutationFn: async ({ 
      visitId, 
      content, 
      authorUserId, 
      noteType = 'general' 
    }: { 
      visitId: string; 
      content: string; 
      authorUserId?: string; 
      noteType?: 'general' | 'follow_up' | 'important';
    }) => {
      if (!companyId) throw new Error('No company selected');
      await VisitService.addNoteToVisit(companyId, visitId, content, authorUserId, noteType);
      return { visitId };
    },
    onSuccess: (data) => {
      toast({
        title: "Not eklendi",
        description: "Not başarıyla ziyarete eklendi.",
      });
      queryClient.invalidateQueries({ queryKey: ['visit-notes', data.visitId] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Not eklenirken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error adding note to visit:', error);
    },
  });

  // Update visit mutation
  const updateVisitMutation = useMutation({
    mutationFn: async ({ 
      visitId, 
      updates 
    }: { 
      visitId: string; 
      updates: Partial<Pick<VisitRecord, 'notes' | 'visit_type' | 'location'>>;
    }) => {
      await VisitService.updateVisit(visitId, updates);
      return { visitId };
    },
    onSuccess: (data) => {
      toast({
        title: "Ziyaret güncellendi",
        description: "Ziyaret bilgileri başarıyla güncellendi.",
      });
      queryClient.invalidateQueries({ queryKey: ['visit-record', data.visitId] });
      queryClient.invalidateQueries({ queryKey: ['visit-records'] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Ziyaret güncellenirken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error updating visit:', error);
    },
  });

  // Delete visit mutation
  const deleteVisitMutation = useMutation({
    mutationFn: async (visitId: string) => {
      await VisitService.deleteVisit(visitId);
      return visitId;
    },
    onSuccess: () => {
      toast({
        title: "Ziyaret silindi",
        description: "Ziyaret başarıyla silindi.",
      });
      queryClient.invalidateQueries({ queryKey: ['visit-records'] });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Ziyaret silinirken bir hata oluştu.",
        variant: "destructive",
      });
      console.error('Error deleting visit:', error);
    },
  });

  return {
    visits,
    isLoading,
    error,
    getVisit,
    getVisitProducts,
    getVisitPhotos,
    getVisitNotes,
    useOpenVisits,
    startVisit: startVisitMutation.mutate,
    endVisit: endVisitMutation.mutate,
    linkVisitToCustomer: linkVisitToCustomerMutation.mutate,
    uploadPhoto: uploadPhotoMutation.mutate,
    addProductToVisit: addProductToVisitMutation.mutate,
    addNoteToVisit: addNoteToVisitMutation.mutate,
    updateVisit: updateVisitMutation.mutate,
    deleteVisit: deleteVisitMutation.mutate,
    isStartingVisit: startVisitMutation.isPending,
    isEndingVisit: endVisitMutation.isPending,
    isLinkingVisit: linkVisitToCustomerMutation.isPending,
    isUploadingPhoto: uploadPhotoMutation.isPending,
    isAddingProduct: addProductToVisitMutation.isPending,
    isAddingNote: addNoteToVisitMutation.isPending,
    isUpdatingVisit: updateVisitMutation.isPending,
    isDeletingVisit: deleteVisitMutation.isPending,
    getTemporaryVisitData,
    isTemporaryVisit,
    getVisitDisplayName,
    isManualVisit,
    isFaceRecognitionVisit,
    getVisitSourceText,
  };
};

const isVisitTemporary = (visit: VisitRecord): boolean => {
  const status = String(visit.status || '').toUpperCase();
  return !visit.customer_id && status === 'PENDING_LABEL';
};

// Hook for unmatched queue (temporary visits) management
export const useUnmatchedQueue = () => {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();

  const { data: temporaryVisits = [], isLoading } = useQuery({
    queryKey: ['temporaryVisits', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const allVisits = await VisitService.getVisits(companyId);
      const tempVisits = allVisits.filter(isVisitTemporary);
      
      return tempVisits.map(visit => ({
        id: visit.id,
        visitId: visit.id,
        timestamp: visit.started_at,
        location: visit.location || 'Bilinmiyor',
        visitType: 'Facey',
        notes: '',
      }));
    },
    enabled: !!companyId,
  });

  const unmatchedItems = temporaryVisits;

  const acceptCandidateMutation = useMutation({
    mutationFn: async ({ itemId, candidateId }: { itemId: string; candidateId: string }) => {
      console.log('Linking visit', itemId, 'to customer', candidateId);
      await VisitService.linkVisitToCustomer(itemId, candidateId);
      return { itemId, candidateId };
    },
    onSuccess: (data) => {
      console.log('Successfully linked visit to customer:', data);
      toast({
        title: "Başarılı",
        description: "Ziyaret başarıyla müşteriye bağlandı.",
      });
      queryClient.invalidateQueries({ queryKey: ['temporaryVisits'] });
      queryClient.invalidateQueries({ queryKey: ['visit-records'] });
    },
    onError: (error) => {
      console.error('Error linking visit to customer:', error);
      toast({
        title: "Hata",
        description: "Ziyaret müşteriye bağlanırken bir hata oluştu.",
        variant: "destructive",
      });
    },
  });

  const createNewCustomerMutation = useMutation({
    mutationFn: async (itemId: string) => {
      // This will be handled by the UI component that opens customer creation modal
      // For now, just mark the item as needing customer creation
      console.log('Create new customer for:', itemId);
      return itemId;
    },
    onSuccess: () => {
      toast({
        title: "Müşteri oluşturma ekranı açılacak",
        description: "Yeni müşteri oluşturduktan sonra ziyaret bağlanacak.",
      });
    },
  });

  const ignoreItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      // New bridge model: mark visit as ignored in visits table.
      const { data, error } = await supabase
        .from('visits')
        .update({ status: 'IGNORED' as any })
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Ziyaret yok sayıldı",
        description: "Geçici ziyaret kuyruktan kaldırıldı.",
      });
      queryClient.invalidateQueries({ queryKey: ['temporaryVisits'] });
    },
  });

  const bulkLinkMutation = useMutation({
    mutationFn: async ({ itemIds, customerId }: { itemIds: string[]; customerId: string }) => {
      // Link each visit through the shared service so template creation stays consistent.
      await Promise.all(itemIds.map((visitId) => VisitService.linkVisitToCustomer(visitId, customerId)));
      return { itemIds, customerId };
    },
    onSuccess: () => {
      toast({
        title: "Toplu bağlama tamamlandı",
        description: "Seçili ziyaretler başarıyla müşteriye bağlandı.",
      });
      queryClient.invalidateQueries({ queryKey: ['temporaryVisits'] });
      queryClient.invalidateQueries({ queryKey: ['visit-records'] });
    },
  });

  return {
    unmatchedItems,
    acceptCandidate: acceptCandidateMutation.mutate,
    createNewCustomer: createNewCustomerMutation.mutate,
    ignoreItem: ignoreItemMutation.mutate,
    bulkLink: bulkLinkMutation.mutate,
    isAccepting: acceptCandidateMutation.isPending,
    isCreating: createNewCustomerMutation.isPending,
    isIgnoring: ignoreItemMutation.isPending,
    isBulkLinking: bulkLinkMutation.isPending,
  };
};
